<template>
  <section class="statement-section">
    <div class="statement-section__header">
      <div class="statement-section__marker">6</div>
      <div>
        <h3 class="statement-section__title">Конкурсные группы</h3>
        <p class="statement-section__description">
          Выберите направления и расставьте приоритеты
        </p>
      </div>
    </div>

    <div v-if="formData.educationBase" class="statement-contests">
      <div v-if="groupedCompetitiveGroups.length" class="statement-department-list">
        <section v-for="department in groupedCompetitiveGroups" :key="department.id" class="statement-department-group">
          <h4 class="statement-department-title">{{ department.name }}</h4>

          <v-expansion-panels v-model="openedPanelsByDepartment[department.id]" variant="accordion" multiple>
            <v-expansion-panel v-for="specialty in department.specialties" :key="specialty.key" :value="specialty.key">
              <v-expansion-panel-title>
                <div class="statement-contest-specialty">
                  <span>{{ specialty.name }} <span class="statement-muted">({{ specialty.code }})</span></span>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="statement-contest-specialty-body">
                  <v-radio-group
                    :model-value="selectedSpecialtyFormKeys[specialty.key]"
                    class="statement-radio-group"
                    label="Форма обучения"
                    @update:model-value="value => setSelectedSpecialtyForm(specialty.key, value)"
                  >
                    <v-radio
                      v-for="formOption in specialty.formOptions"
                      :key="formOption.key"
                      :label="formOption.name"
                      :value="formOption.key"
                    />
                  </v-radio-group>

                  <div v-if="getSelectedSpecialtyForm(specialty.key)" class="statement-contest-details">
                    <div
                      v-for="funding in getFormFundingOptions(getSelectedSpecialtyForm(specialty.key))"
                      :key="funding.value"
                      class="statement-contest-funding-block"
                    >
                      <div class="statement-contest-funding-title">{{ funding.label }}</div>
                      <div class="statement-contest-directions">
                        <v-checkbox
                          v-for="contestOption in getContestOptionsForFunding(getSelectedSpecialtyForm(specialty.key), funding.value)"
                          :key="contestOption.key"
                          :model-value="isContestSelected(contestOption)"
                          :disabled="isContestOptionDisabled(contestOption)"
                          hide-details
                          @update:model-value="checked => toggleContestSelection(contestOption, checked)"
                        >
                          <template #label>
                            <span>
                              {{ getContestOptionLabel(contestOption) }}
                            </span>
                          </template>
                        </v-checkbox>
                      </div>
                    </div>
                  </div>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </section>
      </div>

      <div v-else class="statement-empty">
        Нет доступных конкурсных групп для выбранных условий.
      </div>

      <div v-if="formData.selectedContests.length" class="statement-selected-contests">
        <h4 class="statement-subsection__title">Выбранные конкурсы</h4>
        <div v-for="contest in selectedContestDetails" 
        :key="contest.key" 
        class="statement-selected-contest"
        :competitiveGroup="contest.groupId"
        :direction="contest.group.direction"
        :is-budget="contest.financing === 'budget'"
        >
          <div class="statement-selected-contest__text">
            <div class="statement-selected-contest__title">
              {{ contest.group.name }} <span class="statement-muted">({{ contest.group.code }})</span>
            </div>
            <div class="statement-selected-contest__meta">
              {{ getDepartmentName(contest.group.department_id) }} · {{ contest.group.study_name }} · {{ getFundingLabel(contest.financing) }}
              <template v-if="getGroupQualificationLabel(contest.group)">
                · {{ getGroupQualificationLabel(contest.group) }}
              </template>
            </div>
            <div v-if="getEducationBaseLabel(contest.educationBase || contest.group.base_education)" class="statement-selected-contest__meta">
              База образования: {{ getEducationBaseLabel(contest.educationBase || contest.group.base_education) }}
            </div>
          </div>
          <v-select autocomplete="one-time-code"
            v-model="contest.priority"
            :items="priorityOptions"
            label="Приоритет"
            density="compact"
            variant="outlined"
            hide-details
            @update:model-value="value => updateContestPriority(contest.key, value)"
          />
          <v-tooltip v-if="contest.confirmed" location="top">
            <template #activator="{ props: tooltipProps }">
              <v-icon
                v-bind="tooltipProps"
                icon="mdi-check-circle"
                color="success"
                size="small"
                aria-label="Прием в конкурс подтвержден"
              />
            </template>
            Прием в конкурс подтвержден. Удаление недоступно.
          </v-tooltip>
          <v-btn
            v-else
            icon="mdi-delete-outline"
            variant="text"
            color="error"
            size="small"
            aria-label="Удалить конкурс"
            @click="removeContest(contest.key)"
          />
        </div>
      </div>
    </div>

    <div v-else class="statement-empty">
      Выберите базу образования, чтобы увидеть доступные конкурсные группы.
    </div>
  </section>
</template>

<script>
export default {
  name: 'CompetitiveGroupsSection',
  props: {
    formData: {
      type: Object,
      required: true
    },
    groupedCompetitiveGroups: {
      type: Array,
      required: true
    },
    selectedSpecialtyFormKeys: {
      type: Object,
      required: true
    },
    priorityOptions: {
      type: Array,
      required: true
    },
    selectedContestDetails: {
      type: Array,
      required: true
    },
    openSelectedPanelsToken: {
      type: Number,
      default: 0
    },
    getSelectedSpecialtyForm: {
      type: Function,
      required: true
    },
    setSelectedSpecialtyForm: {
      type: Function,
      required: true
    },
    getFormFundingOptions: {
      type: Function,
      required: true
    },
    getContestOptionsForFunding: {
      type: Function,
      required: true
    },
    isContestSelected: {
      type: Function,
      required: true
    },
    isContestOptionDisabled: {
      type: Function,
      required: true
    },
    toggleContestSelection: {
      type: Function,
      required: true
    },
    getContestOptionLabel: {
      type: Function,
      required: true
    },
    getGroupQualificationLabel: {
      type: Function,
      required: true
    },
    getDepartmentName: {
      type: Function,
      required: true
    },
    getFundingLabel: {
      type: Function,
      required: true
    },
    getEducationBaseLabel: {
      type: Function,
      required: true
    },
    updateContestPriority: {
      type: Function,
      required: true
    },
    removeContest: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      openedPanelsByDepartment: {},
      shouldOpenSelectedPanels: false
    }
  },
  watch: {
    groupedCompetitiveGroups: {
      handler() {
        this.ensureDepartmentPanelState()
        if (this.shouldOpenSelectedPanels) {
          this.$nextTick(() => {
            this.openPanelsForSelectedSpecialties()
          })
        }
      },
      immediate: true
    },
    openSelectedPanelsToken: {
      handler(value) {
        if (!value) {
          return
        }

        this.shouldOpenSelectedPanels = true
        this.$nextTick(() => {
          this.openPanelsForSelectedSpecialties()
        })
      },
      immediate: true
    }
  },
  methods: {
    ensureDepartmentPanelState() {
      const nextState = { ...this.openedPanelsByDepartment }

      this.groupedCompetitiveGroups.forEach((department) => {
        if (!Array.isArray(nextState[department.id])) {
          nextState[department.id] = []
        }
      })

      this.openedPanelsByDepartment = nextState
    },
    openPanelsForSelectedSpecialties() {
      this.ensureDepartmentPanelState()

      if (!this.formData.selectedContests.length) {
        return
      }

      const selectedGroupIds = new Set(
        this.formData.selectedContests.map(contest => String(contest.groupId))
      )
      const openedPanels = {}

      let openedCount = 0

      this.groupedCompetitiveGroups.forEach((department) => {
        const departmentPanels = department.specialties
          .map((specialty) => (
            specialty.groups.some(group => selectedGroupIds.has(String(group.id)))
              ? specialty.key
              : null
          ))
          .filter(key => key !== null)

        openedPanels[department.id] = departmentPanels
        openedCount += departmentPanels.length
      })

      this.openedPanelsByDepartment = {
        ...this.openedPanelsByDepartment,
        ...openedPanels
      }

      if (openedCount > 0) {
        this.shouldOpenSelectedPanels = false
      }
    }
  }
}
</script>

<style>
.statement-contests {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.statement-contest-specialty {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
}

.statement-contest-specialty>span:first-child {
  min-width: 0;
  overflow-wrap: anywhere;
}

.statement-department-list {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.statement-department-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.statement-department-title {
  margin: 0;
  padding-bottom: 8px;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.35;
  color: #1f2937;
  border-bottom: 1px solid #e6e8ee;
}

.statement-contest-specialty-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.statement-contest-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid #e6e8ee;
  border-radius: 8px;
  background: #f9fafb;
}

.statement-contest-funding-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.statement-contest-funding-title {
  font-weight: 700;
  color: #344054;
}

.statement-contest-directions {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.statement-selected-contests {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.statement-selected-contest {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(120px, 160px) 40px;
  gap: 12px;
  align-items: center;
  padding: 12px;
  border: 1px solid #e6e8ee;
  border-radius: 8px;
  background: #ffffff;
}

.statement-selected-contest__text {
  min-width: 0;
}

.statement-selected-contest__title {
  font-weight: 700;
  color: #1f2937;
  overflow-wrap: anywhere;
}

.statement-selected-contest__meta {
  margin-top: 4px;
  color: #667085;
  line-height: 1.4;
}

@media (max-width: 640px) {
  .statement-selected-contest {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
